import { NavLink, Outlet, useParams, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import cn from "classnames";
import { ChevronLeftIcon, LockClosedIcon } from "@heroicons/react/24/outline";
import useProjectStore from "../../store/projectStore";
import useAuthStore from "../../store/authStore";
import { bladesApi } from "../../api/blades";
import LivePreviewPanel from "../../components/LivePreviewPanel";

const TABS = [
  { to: "summary",   label: "Summary" },
  { to: "hydraulic", label: "Hydraulic Layout" },
  { to: "meco",      label: "Meridional Contour" },
  { to: "edges",     label: "Edges" },
  { to: "beta",      label: "Beta Angles" },
  { to: "thickness", label: "Thickness" },
  { to: "cutoff",    label: "Cut-off" },
  { to: "view2d",    label: "2D View" },
  { to: "view3d",    label: "3D View" },
  { to: "curvature", label: "Curvature" },
  { to: "transform", label: "Transform" },
  { to: "export",    label: "Export" },
];

export default function BladeLayout() {
  const { projectId, bladeId } = useParams();
  const { activeDesign, setActiveDesign } = useProjectStore();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [showPreview, setShowPreview] = useState(true);
  const [previewWidth, setPreviewWidth] = useState(440);
  const isDragging = useRef(false);
  const dragStart = useRef({ x: 0, width: 0 });

  // Auto-hide panel on full view tabs (they already show the chart full-screen)
  const isViewTab = /\/(view2d|view3d|curvature)$/.test(location.pathname);

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!isDragging.current) return;
      const delta = dragStart.current.x - e.clientX;
      setPreviewWidth(Math.max(280, Math.min(window.innerWidth * 0.72, dragStart.current.width + delta)));
    };
    const onMouseUp = () => {
      if (!isDragging.current) return;
      isDragging.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  const onDividerMouseDown = (e) => {
    isDragging.current = true;
    dragStart.current = { x: e.clientX, width: previewWidth };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    e.preventDefault();
  };

  useEffect(() => {
    bladesApi.get(bladeId)
      .then(({ data }) => setActiveDesign(data))
      .catch(() => {});
  }, [bladeId, setActiveDesign]);

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Sub-header */}
      <div className="bg-white border-b border-gray-200 px-6 py-0 flex items-center gap-4 shrink-0">
        <button
          onClick={() => navigate(`/projects/${projectId}`)}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 py-3 transition-colors"
        >
          <ChevronLeftIcon className="h-4 w-4" />
          Back
        </button>
        <span className="text-gray-300">|</span>
        <span className="text-sm font-semibold text-gray-700 py-3 border-l-2 border-bh-500 pl-3">
          {activeDesign?.name || "Loading…"}
        </span>
        {activeDesign && (
          <div className="flex items-center gap-2">
            {activeDesign.n_blades != null && (
              <span className="text-xs bg-bh-50 text-bh-700 border border-bh-200 rounded px-2 py-0.5 font-medium">
                {activeDesign.n_blades} blades
              </span>
            )}
            {(() => {
              const { rpm, flow_rate, head } = activeDesign;
              if (!rpm || !flow_rate || !head) return null;
              const nq = rpm * Math.sqrt(flow_rate) / Math.pow(head, 0.75);
              return (
                <span className="text-xs bg-gray-100 text-gray-600 rounded px-2 py-0.5">
                  nq = {nq.toFixed(1)}
                </span>
              );
            })()}
          </div>
        )}
        <nav className="flex gap-0 ml-4">
          {TABS.map(({ to, label }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                cn("px-4 py-3 text-sm font-medium border-b-2 transition-colors",
                  isActive
                    ? "border-bh-600 text-bh-600"
                    : "border-transparent text-gray-500 hover:text-gray-700")
              }
            >
              {label}
            </NavLink>
          ))}
        </nav>

        {!isViewTab && (
          <button
            onClick={() => setShowPreview(p => !p)}
            className="ml-auto text-xs text-gray-400 hover:text-gray-700 border border-gray-200 rounded px-2.5 py-1 bg-white hover:bg-gray-50 transition-colors shrink-0"
            title={showPreview ? "Hide preview panel" : "Show preview panel"}
          >
            {showPreview ? "⊡ Hide preview" : "⊞ Preview"}
          </button>
        )}
      </div>

      {/* Released banner */}
      {activeDesign?.is_released && (
        <div className="bg-amber-50 border-b border-amber-200 px-6 py-2 flex items-center gap-2 shrink-0">
          <LockClosedIcon className="h-4 w-4 text-amber-600 shrink-0" />
          <span className="text-sm text-amber-800 font-medium">
            This design is released and cannot be edited.
          </span>
        </div>
      )}

      {/* Checkout banner — shown when checked out by someone else */}
      {!activeDesign?.is_released && activeDesign?.checked_out_by_id && activeDesign?.checked_out_by_id !== user?.id && (
        <div className="bg-orange-50 border-b border-orange-200 px-6 py-2 flex items-center gap-2 shrink-0">
          <LockClosedIcon className="h-4 w-4 text-orange-600 shrink-0" />
          <span className="text-sm text-orange-800 font-medium">
            Checked out by <span className="font-semibold">{activeDesign.checked_out_by}</span> — read-only until checked in.
          </span>
        </div>
      )}

      {/* Page content — split when preview is visible */}
      <div className="flex-1 flex overflow-hidden">
        <div className="flex-1 min-w-0 overflow-y-auto bg-gray-50">
          <Outlet />
        </div>
        {showPreview && !isViewTab && (
          <>
            {/* Drag handle */}
            <div
              onMouseDown={onDividerMouseDown}
              className="w-1.5 shrink-0 bg-gray-200 hover:bg-bh-400 active:bg-bh-500 cursor-col-resize transition-colors"
            />
            {/* Preview panel */}
            <div className="shrink-0 flex flex-col bg-white overflow-hidden" style={{ width: previewWidth }}>
              <LivePreviewPanel />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
